// Form controls: .field wraps label + control + hint/error; .input/.textarea/.select are the controls.
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  .label {
    font-size: var(--vc-text-sm);
    font-weight: var(--vc-weight-medium);
    color: var(--vc-text-secondary);

    .required {
      color: var(--vc-brand);
      margin-left: 2px;
    }
  }

  .hint {
    font-size: var(--vc-text-xs);
    color: var(--vc-text-muted);
  }

  .error {
    font-size: var(--vc-text-xs);
    color: var(--vc-red);
  }

  .control {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    .adornment {
      position: absolute;
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      color: var(--vc-text-muted);
      pointer-events: none;

      svg {
        width: 16px;
        height: 16px;
      }

      &.left {
        left: 12px;
      }

      &.right {
        right: 12px;
      }

      // Holds a real control (show/hide password); must receive clicks.
      &.interactive {
        pointer-events: auto;

        button {
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          border-radius: var(--vc-radius-sm);
          color: var(--vc-text-muted);
          transition:
            color var(--vc-duration) var(--vc-ease),
            background-color var(--vc-duration) var(--vc-ease);

          &:hover {
            color: var(--vc-text);
            background: var(--vc-gray-soft);
          }

          &:focus-visible {
            @include focus-ring;
          }
        }
      }
    }
  }
}

// Shared control chrome; size, state and adornment modifiers sit on the same element.
.input,
.textarea,
.select {
  width: 100%;
  height: var(--vc-control-height);
  padding: 0 12px;
  border: 1px solid var(--vc-border-strong);
  border-radius: var(--vc-radius-md);
  background: var(--vc-surface);
  color: var(--vc-text);
  font-size: var(--vc-text-md);
  transition:
    border-color var(--vc-duration) var(--vc-ease),
    box-shadow var(--vc-duration) var(--vc-ease);

  &:hover {
    border-color: var(--vc-text-faint);
  }

  &:focus {
    outline: none;
    border-color: var(--vc-brand);
    box-shadow: var(--vc-focus-ring);
  }

  &:disabled {
    background: var(--vc-bg-subtle);
    color: var(--vc-text-muted);
    cursor: not-allowed;
  }

  &.invalid {
    border-color: var(--vc-red) !important;
  }

  &.sm {
    height: var(--vc-control-height-sm);
    font-size: var(--vc-text-sm);
    border-radius: var(--vc-radius-sm);
  }

  &.withLeft {
    padding-left: 36px;
  }

  &.withRight {
    padding-right: 36px;
  }
}

.input,
.textarea {
  &::placeholder {
    color: var(--vc-text-faint);
  }
}

.textarea {
  height: auto;
  min-height: 96px;
  padding: 10px 12px;
  resize: vertical;
  line-height: var(--vc-leading-normal);
}

.select {
  appearance: none;
  padding-right: 34px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%237c8295' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  cursor: pointer;
}

// Checkbox and switch share the .checkRow label wrapper.
.checkRow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: var(--vc-text-md);
  color: var(--vc-text);

  .checkbox {
    appearance: none;
    width: 18px;
    height: 18px;
    border: 1.5px solid var(--vc-border-strong);
    border-radius: 5px;
    background: var(--vc-surface);
    display: grid;
    place-content: center;
    transition: all var(--vc-duration) var(--vc-ease);
    cursor: pointer;
    flex: none;

    &::before {
      content: '';
      width: 10px;
      height: 10px;
      transform: scale(0);
      transition: transform var(--vc-duration) var(--vc-ease);
      clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
      background: #fff;
    }

    &:checked {
      background: var(--vc-brand);
      border-color: var(--vc-brand);

      &::before {
        transform: scale(1);
      }
    }

    &:focus-visible {
      box-shadow: var(--vc-focus-ring);
    }
  }

  .switch {
    position: relative;
    width: 38px;
    height: 22px;
    border-radius: var(--vc-radius-pill);
    background: var(--vc-border-strong);
    transition: background var(--vc-duration) var(--vc-ease);
    flex: none;

    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      box-shadow: var(--vc-shadow-sm);
      transition: transform var(--vc-duration) var(--vc-ease);
    }

    &.switchOn {
      background: var(--vc-brand);

      &::after {
        transform: translateX(16px);
      }
    }

    &:disabled {
      opacity: 0.5;
    }
  }
}
